import { Autocomplete } from 'src/components/Autocomplete';
import { TextField } from 'src/components/TextField';

interface RangeTypeFilterProps {
  label?: string;
  value: string | undefined;
  setValue: (newVal: string) => void;
  valueOnly?: boolean;
  future?: boolean;
  past?: boolean;
  showCurrent?: boolean;
  compareToRange?: string;
  children?: (options, value) => React.ReactNode;
}

const RANGE_OPTIONS = [
  { value: 'week', label: 'Last 7 Days', sort: 0 },
  { value: 'lastWeek', label: 'Last Week', sort: 1 },
  { value: 'thisWeek', label: 'This Week', sort: 2 },
  { value: 'month', label: 'Last 30 Days', sort: 3 },
  { value: 'lastMonth', label: 'Last Month', sort: 4 },
  { value: 'thisMonth', label: 'This Month', sort: 5 },
  { value: 'lastYear', label: 'Last Year', sort: 6 },
  { value: 'thisYear', label: 'This Year', sort: 7 },
  { value: 'thisQuarter', label: 'This Quarter', sort: 8 },
  { value: 'lastQuarter', label: 'Last Quarter', sort: 9 },
  { value: 'allTime', label: 'All Time', sort: 10 },
];

const FUTURE_RANGE_OPTIONS = [
  { value: 'next7Days', label: 'Next 7 Days', sort: 0 },
  { value: 'nextWeek', label: 'Next Week', sort: 2 },
  { value: 'next30Days', label: 'Next 30 Days', sort: 3 },
  { value: 'nextMonth', label: 'Next Month', sort: 5 },
  { value: 'nextQuarter', label: 'Next Quarter', sort: 9 },
];

const MONTHS_AGO_OPTIONS = [
  { value: '1MonthAgo', label: '1 Month Before Range', sort: 0 },
  { value: '2MonthsAgo', label: '2 Months Before Range', sort: 1 },
  { value: '3MonthsAgo', label: '3 Months Before Range', sort: 2 },
  { value: '4MonthsAgo', label: '4 Months Before Range', sort: 3 },
  { value: '5MonthsAgo', label: '5 Months Before Range', sort: 4 },
  { value: '6MonthsAgo', label: '6 Months Before Range', sort: 5 },
  { value: '7MonthsAgo', label: '7 Months Before Range', sort: 6 },
  { value: '8MonthsAgo', label: '8 Months Before Range', sort: 7 },
  { value: '9MonthsAgo', label: '9 Months Before Range', sort: 8 },
  { value: '10MonthsAgo', label: '10 Months Before Range', sort: 9 },
  { value: '11MonthsAgo', label: '11 Months Before Range', sort: 10 },
  { value: '12MonthsAgo', label: '12 Months Before Range', sort: 11 },
];

const WEEK_AGO_OPTIONS = [
  { value: '1WeekAgo', label: '1 Week Before Range', sort: 0 },
  { value: '2WeeksAgo', label: '2 Weeks Before Range', sort: 1 },
  { value: '3WeeksAgo', label: '3 Weeks Before Range', sort: 2 },
  { value: '4WeeksAgo', label: '4 Weeks Before Range', sort: 3 },
  { value: '5WeeksAgo', label: '5 Weeks Before Range', sort: 4 },
  { value: '6WeeksAgo', label: '6 Weeks Before Range', sort: 5 },
  { value: '7WeeksAgo', label: '7 Weeks Before Range', sort: 6 },
  { value: '8WeeksAgo', label: '8 Weeks Before Range', sort: 7 },
  { value: '9WeeksAgo', label: '9 Weeks Before Range', sort: 8 },
  { value: '10WeeksAgo', label: '10 Weeks Before Range', sort: 9 },
  { value: '11WeeksAgo', label: '11 Weeks Before Range', sort: 10 },
  { value: '12WeeksAgo', label: '12 Weeks Before Range', sort: 11 },
];

const QUARTER_AGO_OPTIONS = [
  { value: '1QuarterAgo', label: '1 Quarter Before Range', sort: 0 },
  { value: '2QuartersAgo', label: '2 Quarters Before Range', sort: 1 },
  { value: '3QuartersAgo', label: '3 Quarters Before Range', sort: 2 },
  { value: '4QuartersAgo', label: '4 Quarters Before Range', sort: 3 },
];

const YEAR_AGO_OPTIONS = [
  { value: '1YearAgo', label: '1 Year Before Range', sort: 0 },
  { value: '2YearsAgo', label: '2 Years Before Range', sort: 1 },
  { value: '3YearsAgo', label: '3 Years Before Range', sort: 2 },
  { value: '4YearsAgo', label: '4 Years Before Range', sort: 3 },
  { value: '5YearsAgo', label: '5 Years Before Range', sort: 4 },
];

const THIRTY_DAYS_AGO_OPTIONS = [
  { value: '30DaysAgo', label: '30 Days Before Range', sort: 0 },
];

const SEVEN_DAYS_AGO_OPTIONS = [
  { value: '7DaysAgo', label: '7 Days Before Range', sort: 0 },
];

export const RangeTypeFilter: React.FC<RangeTypeFilterProps> = ({
  label,
  value,
  setValue,
  valueOnly,
  future = false,
  past = false,
  showCurrent = false,
  compareToRange,
  children,
}) => {
  let options: { value: string; label: string; sort: number }[] = [];

  if (compareToRange) {
    switch (compareToRange) {
      case 'month':
      case 'next30Days':
        options = THIRTY_DAYS_AGO_OPTIONS;
        break;
      case 'week':
      case 'next7Days':
        options = SEVEN_DAYS_AGO_OPTIONS;
        break;
      case 'thisMonth':
      case 'lastMonth':
      case 'nextMonth':
        options = MONTHS_AGO_OPTIONS;
        break;
      case 'thisWeek':
      case 'lastWeek':
      case 'nextWeek':
        options = WEEK_AGO_OPTIONS;
        break;
      case 'quarter':
      case 'thisQuarter':
      case 'lastQuarter':
      case 'nextQuarter':
        options = QUARTER_AGO_OPTIONS;
        break;
      case 'year':
      case 'thisYear':
      case 'lastYear':
      case 'nextYear':
        options = YEAR_AGO_OPTIONS;
        break;
    }
  } else {
    if (future) {
      options.push(...FUTURE_RANGE_OPTIONS);
    }

    if (past || (!future && !past)) {
      options.push(...RANGE_OPTIONS);
    }
  }

  options = options.sort((a, b) => a.sort - b.sort);

  if (showCurrent) {
    options = [{ value: 'current', label: 'Current', sort: 0 }, ...options];
  }

  options.sort((a, b) => a.sort - b.sort);

  const selectedOption =
    options.find((option) => option.value === value) || null;

  if (valueOnly) {
    return selectedOption ? <>{selectedOption.label}</> : null;
  }

  if (children && typeof children === 'function') {
    return <>{children(options, value)}</>;
  }

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label}
      value={selectedOption}
      onChange={(_, newValue) => setValue(newValue?.value || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={!value ? 'None' : ''}
        />
      )}
    />
  );
};
